import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useStrings } from '../i18n';
import PanchTatvaButton from '../components/PanchTatvaButton';
import products from '../data/products';
import { validateCartItem } from '../utils/cartUtils';

// Resolve image from the products catalogue by id — never trust the stored value,
// which can be stale or broken (e.g. from an old localStorage entry).
const getProductImage = (itemId) => {
  const found = products.find((p) => p.id === itemId || String(p.id) === String(itemId));
  return found?.image || null;
};

export default function Cart() {
  const sectionRef = useRef([]);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, removeFromCart, updateQuantity } = useCart();
  const strings = useStrings();

  // IntersectionObserver for fade-in animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });
  }, []);

  const total = cart.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  // Defensive filter at render boundary — CartContext already sanitizes, but belt-and-suspenders
  const validCart = cart.filter(validateCartItem);

  if (cart.length === 0) {
    return (
      <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center">
        <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-6">
          Your Cart is Empty
        </h1>
        <p className="text-bodyText text-lg mb-8">
          Add items to get started with your Panch Tatva collection
        </p>
        <PanchTatvaButton onClick={() => navigate('/shop')}>
          Continue Shopping
        </PanchTatvaButton>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-20">
      <h1
        className="text-3xl md:text-4xl font-heading text-headingText mb-8 fade-section"
        ref={(el) => (sectionRef.current[0] = el)}
      >
        {strings.cart?.title || 'Shopping Cart'}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {validCart.map((item, i) => (
            <div
              key={item.id}
              className="bg-cardBg p-4 md:p-6 rounded-lg shadow-lg fade-section"
              ref={(el) => (sectionRef.current[i + 1] = el)}
            >
              <div className="flex items-start gap-4">
                {/* Item Image — resolved from products catalogue, never from stale localStorage */}
                {getProductImage(item.id) && (
                  <img
                    src={getProductImage(item.id)}
                    alt={item.name}
                    className="w-16 h-16 md:w-24 md:h-24 object-contain rounded-lg flex-shrink-0 bg-cardBg p-1"
                  />
                )}

                {/* Item Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-bodyText text-base md:text-lg mb-1 truncate">{item.name}</h3>
                  <p className="text-secondary font-semibold mb-3">₹{item.price}</p>

                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {/* Quantity Control */}
                    <div className="flex items-center gap-1 border-2 border-gray-300 rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                        className="px-3 py-1 hover:bg-primaryBg transition font-bold text-lg"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity || 1}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-10 py-1 text-center bg-primaryBg text-bodyText focus:outline-none"
                      />
                      <button
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                        className="px-3 py-1 hover:bg-primaryBg transition font-bold text-lg"
                      >
                        +
                      </button>
                    </div>

                    {/* Subtotal & Remove */}
                    <div className="flex items-center gap-4">
                      <p className="font-bold text-headingText text-base md:text-lg">
                        ₹{(item.price * (item.quantity || 1)).toLocaleString()}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold transition"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cart Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-32 bg-cardBg rounded-lg p-6 shadow-lg">
            <h2 className="text-xl font-heading text-headingText mb-6">Order Summary</h2>

            {/* Itemized Summary */}
            <div className="space-y-2 mb-6 pb-6 border-b border-gray-300">
              {validCart.map(item => (
                <div key={item.id} className="flex justify-between text-sm text-bodyText">
                  <span>{item.name} x {item.quantity || 1}</span>
                  <span className="font-semibold">₹{(item.price * (item.quantity || 1)).toLocaleString()}</span>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mb-6 pb-6 border-b border-gray-300">
              <div className="flex justify-between text-lg font-bold text-secondary">
                <span>Subtotal</span>
                <span>₹{total.toLocaleString()}</span>
              </div>
            </div>

            {/* Notes */}
            <p className="text-xs text-gray-600 mb-6">
              Shipping and taxes calculated at checkout
            </p>

            {/* Guest sign-in banner */}
            {!isAuthenticated && (
              <div className="mb-4 p-3 bg-surface rounded-xl border border-secondary/30 flex items-center justify-between gap-2">
                <p className="font-sans text-sm text-bodyText">
                  {strings.cart?.guestBanner || 'Sign in to save your cart across devices'}
                </p>
                <Link
                  to="/login"
                  state={{ from: '/cart' }}
                  className="text-sm font-semibold text-secondary hover:underline whitespace-nowrap"
                >
                  {strings.auth?.login || 'Sign in'}
                </Link>
              </div>
            )}

            {/* Checkout Button — available to all users */}
            <PanchTatvaButton onClick={() => navigate('/checkout')} className="w-full block">
              {strings.cart?.checkout || 'Proceed to Checkout'}
            </PanchTatvaButton>

            {/* Continue Shopping */}
            <button
              onClick={() => navigate('/shop')}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 text-bodyText font-semibold hover:border-secondary hover:text-secondary transition"
            >
              Continue Shopping
            </button>

            {/* Trust Badges */}
            <div className="mt-6 pt-6 border-t border-gray-300 space-y-2 text-center text-xs text-gray-600">
              <p>✓ Secure checkout</p>
              <p>✓ Easy returns</p>
              <p>✓ Free shipping on orders above ₹500</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
